// src/utils/generateInvoicePdf.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCHF } from "./formatCHF";

/**
 * generateInvoicePdf
 * - Entrée: invoice + lines + fees (TOUT en centimes)
 * - Sortie: instance jsPDF (doc)
 *
 * NOTE:
 * - Sur certains bundlers (CodeSandbox), jspdf-autotable n'attache PAS doc.autoTable.
 * - La forme robuste est: autoTable(doc, options)
 */
export function generateInvoicePdf({ invoice, lines = [], fees = [] }) {
  const doc = new jsPDF();

  const invNo = invoice?.invoice_number || `MD-${invoice?.id || ""}`;
  const dateStr = invoice?.issued_at || invoice?.created_at || "";

  // Header
  doc.setFontSize(16);
  doc.text("Marché Deluxe", 14, 18);

  doc.setFontSize(10);
  doc.text(`Facture: ${invNo}`, 14, 26);
  doc.text(`Date: ${dateStr}`, 14, 32);

  // Client snapshot
  const y0 = 42;
  doc.setFontSize(11);
  doc.text("Client", 14, y0);
  doc.setFontSize(10);
  doc.text(`${invoice?.customer_name || ""}`, 14, y0 + 6);

  const addr = [
    invoice?.customer_address1,
    invoice?.customer_address2,
    [invoice?.customer_zip, invoice?.customer_city].filter(Boolean).join(" "),
    invoice?.customer_country,
  ].filter(Boolean);

  addr.forEach((l, i) => doc.text(String(l), 14, y0 + 12 + i * 5));

  // Mode
  doc.setFontSize(11);
  doc.text("Mode", 140, y0);
  doc.setFontSize(10);
  doc.text(
    `${invoice?.fulfillment_label || invoice?.fulfillment_mode || ""}`,
    140,
    y0 + 6
  );

  // Table lignes
  const rows = (lines || []).map((l) => {
    const qty = Number(l.qty || 0);
    const unit = Number(l.unit_price_cents || 0);
    const total = Number(l.total_cents || qty * unit);
    return [l.name || "", qty, formatCHF(unit), formatCHF(total)];
  });

  // ✅ Version robuste: autoTable(doc, ...)
  autoTable(doc, {
    startY: 78,
    head: [["Produit", "Qté", "Prix unitaire", "Total"]],
    body: rows,
    styles: { fontSize: 9 },
  });

  // Totaux
  const afterY = (doc.lastAutoTable?.finalY || 78) + 8;

  const subtotal = Number(invoice?.subtotal_cents || 0);
  const feesC = Number(invoice?.fees_cents || 0);
  const vat = Number(invoice?.vat_amount_cents || 0);
  const total = Number(invoice?.total_cents || 0);

  doc.setFontSize(10);
  doc.text(`Sous-total: ${formatCHF(subtotal)}`, 140, afterY);
  if (feesC > 0) doc.text(`Frais: ${formatCHF(feesC)}`, 140, afterY + 6);
  if (vat > 0) doc.text(`TVA: ${formatCHF(vat)}`, 140, afterY + 12);

  doc.setFontSize(12);
  doc.text(`TOTAL: ${formatCHF(total)}`, 140, afterY + 20);

  // Détail frais
  if (Array.isArray(fees) && fees.length > 0) {
    const yFees = afterY + 32;
    doc.setFontSize(10);
    doc.text("Détail des frais:", 14, yFees);
    fees.forEach((f, i) => {
      doc.text(
        `- ${f.label || f.code || "Frais"}: ${formatCHF(
          Number(f.amount_cents || 0)
        )}`,
        14,
        yFees + 6 + i * 5
      );
    });
  }

  return doc;
}
